import { config } from './config';
import { ApiError, type DetailErreur } from './apiError';
import { demoActive } from './modeDemo';

/**
 * CLIENT D'API — un seul point de passage pour tous les appels.
 *
 *  * `credentials: 'include'` : la session SSO vit dans le cookie httpOnly
 *    `igd_session`, que le navigateur ne joint qu'à cette condition.
 *  * En développement, l'identité passe par l'en-tête `X-Debug-User` (voir
 *    `stores/auth.ts`) : on la pose ici pour qu'AUCUN appel ne l'oublie — un
 *    téléchargement sans elle tomberait en 401.
 *  * Un 401 en cours de navigation (session expirée) renvoie à /login ; la
 *    coquille branche le gestionnaire via `surNonAuthentifie`.
 *  * MODE DÉMO : chaque appel est servi par le serveur de démonstration du
 *    navigateur (`demo/serveur.ts`), jamais par le réseau. Il est chargé à la
 *    demande : qui n'entre jamais en démo ne le télécharge pas.
 */

export { ApiError, type DetailErreur };

const DEV_EMAIL_KEY = 'igd.devEmail';

/** Identité de développement mémorisée (mode `devLogin` uniquement). */
export function lireIdentiteDev(): string | null {
  try {
    return localStorage.getItem(DEV_EMAIL_KEY);
  } catch {
    return null;
  }
}
export function memoriserIdentiteDev(email: string | null): void {
  try {
    if (email) localStorage.setItem(DEV_EMAIL_KEY, email);
    else localStorage.removeItem(DEV_EMAIL_KEY);
  } catch {
    // Stockage indisponible (navigation privée stricte) : la session dev ne
    // survivra pas au rechargement, rien de plus grave.
  }
}

function enTetesAuth(): Record<string, string> {
  const email = lireIdentiteDev();
  return email ? { 'X-Debug-User': email } : {};
}

let gestionnaire401: (() => void) | null = null;
/** Branché une fois par la coquille : que faire quand la session a expiré. */
export function surNonAuthentifie(fn: () => void): void {
  gestionnaire401 = fn;
}

/**
 * Chemins dont le 401 est une RÉPONSE attendue, pas une expiration : `/me` sert
 * justement à savoir si l'on est connecté. Rediriger là créerait une boucle.
 */
const SANS_REDIRECTION_401 = ['/me', '/auth/'];

function signaler401(path: string): void {
  if (SANS_REDIRECTION_401.some((p) => path.startsWith(p))) return;
  gestionnaire401?.();
}

async function lireErreur(res: Response, path: string): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  let data: { error?: string; message?: string; details?: DetailErreur[] } | null = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (res.status === 401) signaler401(path);
  const message =
    data?.error ??
    data?.message ??
    (res.status === 503 ? 'Service momentanément indisponible.' : `Erreur ${res.status}`);
  return new ApiError(res.status, message, Array.isArray(data?.details) ? data.details : []);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (demoActive()) {
    const { repondreDemo } = await import('@/demo/serveur');
    // Une copie : l'écran ne doit pas partager d'objets avec le « serveur ».
    const reponse = await repondreDemo(method, path, body === undefined ? undefined : JSON.parse(JSON.stringify(body)));
    return (reponse === undefined ? undefined : JSON.parse(JSON.stringify(reponse))) as T;
  }
  let res: Response;
  try {
    res = await fetch(`${config.API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...enTetesAuth(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Réseau coupé ou API arrêtée : un message lisible plutôt que « Failed to fetch ».
    throw new ApiError(0, "Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.");
  }
  if (!res.ok) throw await lireErreur(res, path);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Nom du fichier annoncé par l'API (RFC 6266 : `filename*` UTF-8 d'abord). */
function nomFichier(disposition: string | null, repli: string): string {
  if (!disposition) return repli;
  const etoile = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (etoile?.[1]) {
    try {
      return decodeURIComponent(etoile[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* on retombe sur filename= */
    }
  }
  const simple = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return simple?.[1]?.trim() || repli;
}

/**
 * TÉLÉCHARGER un fichier produit par l'API (export paie, reporting).
 *
 * Un simple `<a href>` ne porterait ni le corps d'un POST ni l'en-tête de dev,
 * et une erreur 409 (contrôle bloquant) s'afficherait comme une page JSON brute.
 * On passe donc par fetch → blob → lien temporaire.
 */
async function telecharger(method: 'GET' | 'POST', path: string, body?: unknown, repli = 'export'): Promise<string> {
  if (demoActive()) {
    const { fichierDemo } = await import('@/demo/serveur');
    const f = await fichierDemo(method, path, body);
    // BOM : Excel lit alors le CSV en UTF-8 (accents des noms et des libellés).
    enregistrerBlob(new Blob([String.fromCharCode(0xfeff), f.contenu], { type: f.type }), f.nom);
    return f.nom;
  }
  let res: Response;
  try {
    res = await fetch(`${config.API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...enTetesAuth(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, "Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.");
  }
  if (!res.ok) throw await lireErreur(res, path);

  const nom = nomFichier(res.headers.get('Content-Disposition'), repli);
  enregistrerBlob(await res.blob(), nom);
  return nom;
}

/** Propose le fichier au navigateur par un lien temporaire. */
function enregistrerBlob(blob: Blob, nom: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Libéré au tour suivant : certains navigateurs lisent l'URL après le clic.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  telecharger,
};

/** Construit une chaîne de requête en ignorant les valeurs vides. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Message affichable pour n'importe quelle erreur attrapée. */
export function messageErreur(e: unknown, repli = 'Une erreur est survenue.'): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return repli;
}

/**
 * Les `details` de l'API rangés par CHAMP, pour s'afficher sous le bon champ.
 * Le chemin peut arriver sous plusieurs formes selon la couche qui l'a produit
 * (zod : `body.dateDeplacement`, `corrections/distanceAllerKm`, contrôle métier :
 * `dateDeplacement`) : on garde le dernier segment, qui est le nom du champ.
 */
export function erreursParChamp(e: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(e instanceof ApiError)) return out;
  for (const d of e.details) {
    const champ = String(d.path ?? '')
      .split(/[./[\]]+/)
      .filter(Boolean)
      .pop();
    if (champ && !out[champ]) out[champ] = d.message;
  }
  return out;
}
